import { Tooltip } from "antd";
import React, { useRef, useState } from "react";

type ImageRendererProps = {
    src: string;
    magnifierHeight?: number;
    magnifierWidth?: number;
    zoomLevel?: number;
    alt?: string;
    onHover?: () => void;
};

const ImageRenderer: React.FC<ImageRendererProps> = ({
    src,
    magnifierHeight = 250,
    magnifierWidth = 250,
    zoomLevel = 2,
    alt = "magnified image",
    onHover
}) => {
    const imageRef = useRef<HTMLImageElement>(null);
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hasTrackedRef = useRef(false);
    const [[x, y], setXY] = useState<[number, number]>([0, 0]);
    const [[imgWidth, imgHeight], setSize] = useState<[number, number]>([0, 0]);
    const [showMagnifier, setShowMagnifier] = useState(false);

    const magnifierContent = (
        <div
            className="rounded-lg ring-1 ring-black/5"
            style={{
                height: `${magnifierHeight}px`,
                width: `${magnifierWidth}px`,
                backgroundColor: "white",
                backgroundImage: `url('${src}')`,
                backgroundRepeat: "no-repeat",
                backgroundSize: `${imgWidth * zoomLevel}px ${imgHeight * zoomLevel}px`,
                backgroundPositionX: `${-x * zoomLevel + magnifierWidth / 2}px`,
                backgroundPositionY: `${-y * zoomLevel + magnifierHeight / 2}px`
            }}
        />
    );

    return (
        <Tooltip
            open={showMagnifier}
            placement="left"
            color="transparent"
            overlayInnerStyle={{ padding: 0, boxShadow: "none" }}
            align={{ offset: [-20, 0] }}
            title={magnifierContent}>
            <img
                key={src}
                ref={imageRef}
                src={src}
                alt={alt}
                className="flex h-full w-full object-contain"
                onMouseEnter={(e) => {
                    const elem = e.currentTarget;
                    const { width, height } = elem.getBoundingClientRect();
                    setSize([width, height]);
                    setShowMagnifier(true);

                    if (onHover && !hasTrackedRef.current) {
                        hoverTimeoutRef.current = setTimeout(() => {
                            if (!hasTrackedRef.current) {
                                hasTrackedRef.current = true;
                                onHover();
                            }
                        }, 5000);
                    }
                }}
                onMouseMove={(e) => {
                    const elem = e.currentTarget;
                    const { top, left } = elem.getBoundingClientRect();
                    const x = e.pageX - left - window.pageXOffset;
                    const y = e.pageY - top - window.pageYOffset;
                    setXY([x, y]);
                }}
                onMouseLeave={() => {
                    setShowMagnifier(false);
                    if (hoverTimeoutRef.current) {
                        clearTimeout(hoverTimeoutRef.current);
                        hoverTimeoutRef.current = null;
                    }
                    hasTrackedRef.current = false;
                }}
            />
        </Tooltip>
    );
};

export default ImageRenderer;
